import React, {useEffect, useMemo, useRef, memo} from 'react';
import {Props} from './ac-backdrop.proptypes';
import clsx from 'clsx';

// Imports => Utilities
import {AcIsSet, AcIsFunction} from '@asdf-utils';

const _CLASSES = {
  MAIN: 'ac-backdrop',
  VISIBLE: 'ac-backdrop--visible',
};

const AcBackdrop = memo(
  ({
    visible = false,
    swipeable = false,
    distance = 0.35,
    callback = () => {},
    className,
    ...rest
  }) => {
    const $backdrop = useRef(null);
    const $swipeDelay = useRef(null);
    const $direction = useRef({
      start: {
        x: 0,
        y: 0,
      },
      end: {
        x: 0,
        y: 0,
      },
    });

    const resetParameters = () => {
      if ($direction && $direction.current) {
        $direction.current = {
          start: {
            x: 0,
            y: 0,
          },
          end: {
            x: 0,
            y: 0,
          },
        };
      }
    };

    const handleCallback = event => {
      if (event && event.preventDefault) event.preventDefault();
      if (event && event.stopPropagation) event.stopPropagation();
      if (event && event.persist) event.persist();

      if (AcIsSet(callback) && AcIsFunction(callback)) callback(event);
    };

    const handleTouchEvent = () => {
      if (!swipeable) return;

      const {start, end} = $direction.current;

      const diff = end.x - start.x;
      const minDiff = window.outerWidth * distance;

      if (end.x > start.x && diff >= minDiff) {
        handleCallback();
        resetParameters();
      }
    };

    const handleTouchStartEvent = event => {
      if (event && event.persist) event.persist();

      const touchX = event.changedTouches[0].clientX;
      const touchY = event.changedTouches[0].clientY;

      $direction.current.start.x = touchX;
      $direction.current.start.y = touchY;
    };

    const handleTouchEndEvent = event => {
      if (event && event.persist) event.persist();

      const touchX = event.changedTouches[0].clientX;
      const touchY = event.changedTouches[0].clientY;

      $direction.current.end.x = touchX;
      $direction.current.end.y = touchY;

      if ($swipeDelay.current) clearTimeout($swipeDelay.current);
      $swipeDelay.current = setTimeout(() => {
        setTimeout(() => {
          window.requestAnimationFrame(() => {
            handleTouchEvent();
          });
        }, 1000 / 60);
      }, 50);
    };

    const onTouchDevice = useMemo(() => {
      const result = 'ontouchstart' in document.documentElement;

      return result;
    }, []);

    const addEvents = () => {
      removeEvents().then(() => {
        if ($backdrop && $backdrop.current && onTouchDevice && window.outerWidth <= 768) {
          window.addEventListener('touchstart', handleTouchStartEvent, false);
          window.addEventListener('touchend', handleTouchEndEvent, false);
        }
      });
    };

    const removeEvents = () => {
      return new Promise((resolve, reject) => {
        if ($backdrop && $backdrop.current && onTouchDevice && window.outerWidth <= 768) {
          window.removeEventListener('touchstart', handleTouchStartEvent, false);
          window.removeEventListener('touchend', handleTouchEndEvent, false);
          resolve();
        }
      });
    };

    useEffect(() => {
      if (swipeable && visible) addEvents();
      else removeEvents();
    }, [swipeable, visible]);

    const getMainClassNames = useMemo(() => {
      return clsx(_CLASSES.MAIN, visible && _CLASSES.VISIBLE, className);
    }, [visible, className]);

    return (
      <div
        aria-modal={true}
        role={'dialog'}
        className={getMainClassNames}
        ref={$backdrop}
        {...rest}
        onClick={handleCallback}
      />
    );
  }
);

AcBackdrop.displayName = 'AcBackdrop';
AcBackdrop.propTypes = Props;

export {AcBackdrop};
export default AcBackdrop;
